import { run, state, waitFor } from '@mvf/product-components/sagas';
import { historyUpdated } from '@mvf/product-components/historyListener';
import { CancelEffect, all, cancel, delay, put } from 'redux-saga/effects';
import { Task } from 'redux-saga';
import { forkTask } from '../../Global/Sagas/forkTask';
import {
  ExecuteType,
  setTestsCompleted,
  loadLinkShieldSession,
  testRecaptcha,
  testAnura,
  startRedirect,
  testCheq,
  recordProviderResultInState,
} from './loadingBarReducer';
import {
  areActiveTestsCompleted,
  getProviderResults,
} from './loadingBarSelectors';
import { Environment } from '../../Global/Environment';
import { createNotification } from '@mvf/product-components/messageNotifier';
import { Severity } from '@mvf/product-components/enums';
import { filter, map, pickBy, values } from 'lodash-es';
import { context, trace, Span } from '@opentelemetry/api';
import {
  IAcquireTestResultsBody,
  IBypassOptions,
  IClientResult,
  IJwtLinkShieldSessionSchema,
  IAcquireAllTestResultsBody,
} from 'appwiki-link-shield-proxy';
import { traceContext } from '../../Global/Sagas/traceContext';
import { RedirectResponse } from './LoadingBarActionTypes';

const tracer = trace.getTracer('features.sagas.loading_bar');

let cancelableTasks: {
  activeTestsSaga: Task;
  bypassTestAfterMaxDuration: Task;
  bypassTestOnExitIntent: Task;
};

type CancelableTasks = typeof cancelableTasks;
type IBypassTestTaskNames = {
  [K in keyof CancelableTasks]: K extends `bypassTest${string}` ? K : never;
}[keyof CancelableTasks];

function* cancelTasksExcept(
  name: keyof CancelableTasks,
): Generator<CancelEffect, void, void> {
  const tasksToCancel = pickBy(cancelableTasks, (v, key) => key !== name);
  yield cancel(values(tasksToCancel));
}

const endSpanOnTaskEnd = (span: Span, task: Task) => {
  void task.toPromise().finally(() => {
    span.setAttribute('isCancelled', task.isCancelled());
    span.end();
  });
};

function* hasBypassHashOnPath() {
  const { payload } = yield* waitFor(historyUpdated);
  return payload.location.hash === '#bypass';
}

const fetchDestinationRedirect = async (
  link: string,
  results: IAcquireTestResultsBody[],
) => {
  const body: IAcquireAllTestResultsBody = {
    tests: results,
  };

  const response = await fetch(link, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const maybeRedirect = RedirectResponse.safeParse(await response.json());
  if (!maybeRedirect.success) {
    return createNotification({
      severity: Severity.ERROR,
      message: 'Unexpected server error; invalid redirect destination',
    });
  }

  return startRedirect(maybeRedirect.data);
};

const buildLinkWithSuffix = (link: string, suffix: string) => {
  const url = new URL(link);
  url.pathname = `${url.pathname}${suffix}`;

  const expectedQueryParams: Record<keyof IBypassOptions, true> = {
    attempt: true,
    isHoneypotLink: true,
    bypassKey: true,
  };

  /**
   * Apple OS 17 and older does not allow filter to be called on searchParam keys, e.g.
   *
   *  url.searchParams.keys().filter(...)
   *
   * results in this error on older apple devices
   *
   *  "TypeError: n.searchParams.keys().filter is not a function"
   *
   * workaround is to spread keys into a new array with [...url.searchParams.keys()] and use that instead
   */
  const searchParamKeys = [...url.searchParams.keys()];
  const deleteQueryParams = filter(
    searchParamKeys,
    (key) => key in expectedQueryParams === false,
  );

  for (const key of deleteQueryParams) {
    url.searchParams.delete(key);
  }

  return url.toString();
};

const buildRunActiveTestSaga = (
  testAction: typeof testRecaptcha | typeof testAnura | typeof testCheq,
  triggerType: ExecuteType,
  providerName: IAcquireTestResultsBody['provider'],
) =>
  function* () {
    const activeContext = yield* traceContext();

    const {
      payload: { provider },
    } = yield* waitFor(testAction);

    const runSpan = tracer.startSpan(
      `test.run.${providerName.toLowerCase()}`,
      {},
      activeContext,
    );

    let result: IClientResult;
    if (provider.status === 'ERROR') {
      result = provider;
    } else {
      result = yield* run(provider.instance, triggerType);
    }

    runSpan.end();

    yield put(
      recordProviderResultInState({
        provider: providerName,
        result,
      }),
    );
  };

type ProviderType = IJwtLinkShieldSessionSchema['activeTests'][number]['type'];

type ProviderExecutors = Record<ProviderType, () => Generator>;

function* activeTestsSaga(
  linkShieldSession: IJwtLinkShieldSessionSchema,
  triggerType: ExecuteType,
) {
  const activeContext = yield* traceContext();
  const providerExecutors: ProviderExecutors = {
    RECAPTCHA: buildRunActiveTestSaga(testRecaptcha, triggerType, 'RECAPTCHA'),
    ANURA: buildRunActiveTestSaga(testAnura, triggerType, 'ANURA'),
    CHEQ: buildRunActiveTestSaga(testCheq, triggerType, 'CHEQ'),
  };

  const activeExecutors = map(linkShieldSession.activeTests, ({ type }) =>
    providerExecutors[type](),
  );

  yield all(activeExecutors);
  yield put(setTestsCompleted());
  yield* cancelTasksExcept('activeTestsSaga');

  const destination = buildLinkWithSuffix(linkShieldSession.link, '/done');

  const providerResults = yield* state(getProviderResults);

  const action = yield* run(
    context.bind(activeContext, fetchDestinationRedirect),
    destination,
    providerResults,
  );

  yield put(action);
}

function* executeActiveTestsSaga(
  linkShieldSession: IJwtLinkShieldSessionSchema,
) {
  const activeContext = yield* traceContext();

  if (yield* hasBypassHashOnPath()) {
    return;
  }

  const span = tracer.startSpan('activeTests.execute', {}, activeContext);

  cancelableTasks.activeTestsSaga = yield* forkTask(
    [{ trace: activeContext }, activeTestsSaga],
    linkShieldSession,
    ExecuteType.TIMEOUT,
  );

  endSpanOnTaskEnd(span, cancelableTasks.activeTestsSaga);
}

function* bypassTestSaga(
  taskName: IBypassTestTaskNames,
  linkShieldSession: IJwtLinkShieldSessionSchema,
  delaySeconds?: number,
) {
  const activeContext = yield* traceContext();

  if (delaySeconds) {
    yield delay(delaySeconds * 1000);
  }

  if (yield* state(areActiveTestsCompleted)) {
    return;
  }

  yield* cancelTasksExcept(taskName);

  const destination = buildLinkWithSuffix(
    linkShieldSession.bypassLink,
    '/done',
  );

  const providerResults = yield* state(getProviderResults);

  const action = yield* run(
    context.bind(activeContext, fetchDestinationRedirect),
    destination,
    providerResults,
  );

  yield put(action);
}

function* testBypassAfterMaxDurationSaga(
  linkShieldSession: IJwtLinkShieldSessionSchema,
) {
  const activeContext = yield* traceContext();

  if (yield* hasBypassHashOnPath()) {
    return;
  }

  const span = tracer.startSpan('test.bypass.max_duration', {}, activeContext);

  cancelableTasks.bypassTestAfterMaxDuration = yield* forkTask(
    [{ trace: activeContext }, bypassTestSaga],
    'bypassTestAfterMaxDuration',
    linkShieldSession,
    Environment.MAX_DURATION_SECONDS,
  );

  endSpanOnTaskEnd(span, cancelableTasks.bypassTestAfterMaxDuration);
}

function* bypassTestOnExitIntent(
  span: Span,
  linkShieldSession: IJwtLinkShieldSessionSchema,
) {
  while (true) {
    if (yield* hasBypassHashOnPath()) {
      span.addEvent('bypass hash detected');
      break;
    } else {
      span.addEvent('current location does not have bypass hash');
    }
  }

  yield* run(bypassTestSaga, 'bypassTestOnExitIntent', linkShieldSession);
}

function* testBypassOnNavigationSaga(
  linkShieldSession: IJwtLinkShieldSessionSchema,
) {
  const activeContext = yield* traceContext();

  const span = tracer.startSpan('test.bypass.exit_intent', {}, activeContext);

  cancelableTasks.bypassTestOnExitIntent = yield* forkTask(
    [{ trace: activeContext }, bypassTestOnExitIntent],
    span,
    linkShieldSession,
  );

  endSpanOnTaskEnd(span, cancelableTasks.bypassTestOnExitIntent);
}

export function* loadingBarSagas() {
  const { payload: linkShieldSession } = yield* waitFor(loadLinkShieldSession);

  cancelableTasks = {
    activeTestsSaga: yield* forkTask(() => {}),
    bypassTestAfterMaxDuration: yield* forkTask(() => {}),
    bypassTestOnExitIntent: yield* forkTask(() => {}),
  };

  yield all([
    executeActiveTestsSaga(linkShieldSession),
    testBypassOnNavigationSaga(linkShieldSession),
    testBypassAfterMaxDurationSaga(linkShieldSession),
  ]);
}
